import {
  ASSISTANT_NAME,
  CONTACT_EMAIL,
  PUBLISHER_NAME,
  PUBLISHER_URL,
  ROUTES,
  SITE_NAME,
  SITE_TAGLINE,
  SITE_URL,
} from '../constants/config';
import { SUBSTANCE_CATEGORIES } from '../constants/effects';
import { buildElementPath, matchRoute } from '../utils/routeUtils';
import {
  findElementBySlug,
  findReactionPageBySlug,
  getAlternateName,
  getElements,
  getPageIndexingPolicy,
  getReactionPages,
} from './sitePages';
import { ELEMENT_PAGE_FACTS } from './elementPageFacts';
import { findWorksheetBySlug } from './worksheets';

export const SOCIAL_IMAGE_PATH = '/og-image.png';
const MAX_DESCRIPTION_LENGTH = 158;

function shortenDescription(text) {
  if (text.length <= MAX_DESCRIPTION_LENGTH) return text;
  return `${text.slice(0, MAX_DESCRIPTION_LENGTH - 1).replace(/\s+\S*$/, '')}…`;
}

const absoluteUrl = (path) => `${SITE_URL}${path === '/' ? '/' : path}`;

const SITE_SCHEMAS = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteUrl('/logo512.png'),
    email: CONTACT_EMAIL,
    parentOrganization: { '@type': 'Organization', name: PUBLISHER_NAME, url: PUBLISHER_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: 'en',
    publisher: { '@type': 'Organization', name: PUBLISHER_NAME, url: PUBLISHER_URL },
  },
];

function buildBreadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map(({ name, path }, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name,
      item: absoluteUrl(path),
    })),
  };
}

const KID_AUDIENCE = { '@type': 'EducationalAudience', educationalRole: 'student', audienceType: 'Children aged 7 to 12' };

function describeLab() {
  return {
    title: `${SITE_TAGLINE}: Mix Elements | ${SITE_NAME}`,
    description:
      'Mix any two of the 118 elements, plus water and acid, and watch what happens. Fizzy the kid scientist explains every reaction in simple words. Free and safe.',
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: `${SITE_NAME} Mixing Lab`,
        url: SITE_URL,
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Any',
        isAccessibleForFree: true,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        audience: KID_AUDIENCE,
        description: 'A virtual chemistry lab where kids mix elements from the periodic table and see what happens.',
      },
    ],
  };
}

// Each family with its article, as it reads in a sentence ("Sodium is element 11, an alkali metal").
const ELEMENT_FAMILY_PHRASES = Object.freeze({
  [SUBSTANCE_CATEGORIES.ALKALI_METAL]: 'an alkali metal',
  [SUBSTANCE_CATEGORIES.ALKALINE_EARTH_METAL]: 'an alkaline earth metal',
  [SUBSTANCE_CATEGORIES.TRANSITION_METAL]: 'a transition metal',
  [SUBSTANCE_CATEGORIES.POST_TRANSITION_METAL]: 'a post-transition metal',
  [SUBSTANCE_CATEGORIES.METALLOID]: 'a metalloid',
  [SUBSTANCE_CATEGORIES.NONMETAL]: 'a nonmetal',
  [SUBSTANCE_CATEGORIES.HALOGEN]: 'a halogen',
  [SUBSTANCE_CATEGORIES.NOBLE_GAS]: 'a noble gas',
  [SUBSTANCE_CATEGORIES.LANTHANIDE]: 'a lanthanide',
  [SUBSTANCE_CATEGORIES.ACTINIDE]: 'an actinide',
});

export function createElementPageDescription(element) {
  const alternateName = getAlternateName(element);
  const name = alternateName
    ? `${element.name} (${element.symbol}), also spelled ${alternateName.toLowerCase()},`
    : `${element.name} (${element.symbol})`;
  const family = ELEMENT_FAMILY_PHRASES[element.category];
  return `${name} is element ${element.atomicNumber}, ${family}. ${ELEMENT_PAGE_FACTS[element.symbol]} Mix it in our free kids' lab!`;
}

function describeElement(slug) {
  const element = findElementBySlug(slug);
  if (!element) return null;
  const path = buildElementPath(element);
  const alternateName = getAlternateName(element);
  return {
    title: `${element.name} (${element.symbol}) for Kids: Facts and Reactions | ${SITE_NAME}`,
    description: createElementPageDescription(element),
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: `${element.name} (${element.symbol})`,
        url: absoluteUrl(path),
        learningResourceType: 'Fact sheet',
        educationalLevel: 'Primary school',
        audience: KID_AUDIENCE,
        about: { '@type': 'Thing', name: element.name, alternateName: alternateName ?? element.symbol },
      },
      buildBreadcrumbSchema([
        { name: 'Lab', path: ROUTES.LAB },
        { name: 'Elements', path: ROUTES.ELEMENTS },
        { name: element.name, path },
      ]),
    ],
  };
}

function describeReaction(slug) {
  const page = findReactionPageBySlug(slug);
  if (!page) return null;
  const { reaction, first, second, path } = page;
  return {
    title: `${first.name} + ${second.name}: What Happens? | ${SITE_NAME}`,
    description: shortenDescription(`What happens when you mix ${first.name.toLowerCase()} and ${second.name.toLowerCase()}? ${reaction.explanation}`),
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: `What happens when you mix ${first.name.toLowerCase()} and ${second.name.toLowerCase()}?`,
        url: absoluteUrl(path),
        author: { '@type': 'Organization', name: SITE_NAME },
        audience: KID_AUDIENCE,
        image: absoluteUrl(SOCIAL_IMAGE_PATH),
      },
      buildBreadcrumbSchema([
        { name: 'Lab', path: ROUTES.LAB },
        { name: 'Famous reactions', path: ROUTES.REACTIONS },
        { name: `${first.name} + ${second.name}`, path },
      ]),
    ],
  };
}

function describeWorksheet(slug) {
  const worksheet = findWorksheetBySlug(slug);
  if (!worksheet) return null;
  return {
    title: `${worksheet.title}: Free Printable Chemistry Worksheet | ${SITE_NAME}`,
    description: worksheet.summary,
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: worksheet.title,
        learningResourceType: 'Worksheet',
        educationalLevel: 'Primary school',
        audience: KID_AUDIENCE,
        timeRequired: `PT${worksheet.minutes}M`,
        isAccessibleForFree: true,
      },
    ],
  };
}

function describeWorksheetAnswers(slug) {
  const worksheet = findWorksheetBySlug(slug);
  if (!worksheet?.hasAnswers) return null;
  return {
    title: `Answer Key: ${worksheet.title} Worksheet | ${SITE_NAME}`,
    description: `Answers for the free ${worksheet.title} chemistry worksheet from ${SITE_NAME}, for parents and teachers to check children's work.`,
  };
}

const STATIC_PAGES = {
  parents: () => ({
    title: `For Parents and Teachers: Lesson Ideas and Free Worksheets | ${SITE_NAME}`,
    description: `How to use the ${SITE_NAME} virtual chemistry lab at home and in class, with lesson ideas, free printable worksheets and notes on safety and privacy.`,
  }),
  about: () => ({
    title: `About ${SITE_NAME}: A Free Science Lab for Curious Kids`,
    description: `${SITE_NAME}, a project of ${PUBLISHER_NAME}, is a free virtual chemistry lab for kids. Meet ${ASSISTANT_NAME} and see how we check our chemistry.`,
  }),
  elements: () => ({
    title: `Periodic Table for Kids: All ${getElements().length} Elements | ${SITE_NAME}`,
    description:
      'Explore all 118 elements of the periodic table with simple facts for kids, and find out what each one reacts with in our virtual lab.',
  }),
  reactions: () => ({
    title: `Famous Chemical Reactions for Kids | ${SITE_NAME}`,
    description: `${getReactionPages().length} famous chemical reactions explained for kids: what happens, why it happens, the equation and how scientists stay safe.`,
  }),
  contact: () => ({
    title: `Contact ${SITE_NAME}`,
    description: `Get in touch with the ${SITE_NAME} team with questions, ideas or corrections.`,
  }),
  privacy: () => ({
    title: `Privacy Policy | ${SITE_NAME}`,
    description: `How ${SITE_NAME} handles privacy, cookies and advertising on our site for kids.`,
  }),
  terms: () => ({
    title: `Terms of Use | ${SITE_NAME}`,
    description: `The rules for using ${SITE_NAME}, a pretend chemistry lab for learning.`,
  }),
};

const NOT_FOUND_META = {
  title: `Page not found | ${SITE_NAME}`,
  description: 'This page does not exist. Head back to the lab to keep experimenting!',
  noIndex: true,
};

/**
 * Title, description, canonical address and structured data for any page path.
 */
export function getPageMeta(path) {
  const route = matchRoute(path);
  let meta = null;
  if (route.page === 'lab') meta = describeLab();
  else if (route.page === 'element') meta = describeElement(route.slug);
  else if (route.page === 'reaction') meta = describeReaction(route.slug);
  else if (route.page === 'worksheet') meta = describeWorksheet(route.slug);
  else if (route.page === 'worksheet-answers') meta = describeWorksheetAnswers(route.slug);
  else if (STATIC_PAGES[route.page]) meta = STATIC_PAGES[route.page]();

  if (!meta) return { ...NOT_FOUND_META, canonicalUrl: null, imageUrl: absoluteUrl(SOCIAL_IMAGE_PATH), schemas: SITE_SCHEMAS };
  const canonicalPath = route.page === 'lab' ? '/' : path.replace(/\/+$/, '');
  return {
    noIndex: !getPageIndexingPolicy().isPathIndexable(path),
    ...meta,
    canonicalUrl: absoluteUrl(canonicalPath),
    imageUrl: absoluteUrl(SOCIAL_IMAGE_PATH),
    schemas: [...SITE_SCHEMAS, ...(meta.schemas ?? [])],
  };
}
